import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { daaraService, Daara, DaaraComment } from '../services/daaraService';
import { useAuth } from '../context/AuthContext';
import api from '../config/api';

export const DaaraDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [daara, setDaara] = useState<Daara | null>(null);
  const [commentaires, setCommentaires] = useState<DaaraComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [liking, setLiking] = useState(false);

  const JOURS = [
    { value: 'lundi', label: 'Lundi' },
    { value: 'mardi', label: 'Mardi' },
    { value: 'mercredi', label: 'Mercredi' },
    { value: 'jeudi', label: 'Jeudi' },
    { value: 'vendredi', label: 'Vendredi' },
    { value: 'samedi', label: 'Samedi' },
    { value: 'dimanche', label: 'Dimanche' },
  ];

  useEffect(() => {
    if (id) {
      fetchDaara();
      fetchCommentaires();
    }
  }, [id]);

  const fetchDaara = async () => {
    if (!id) return;
    try {
      const data = await daaraService.getById(id);
      setDaara(data);
    } catch (error) {
      console.error('Erreur:', error);
      navigate('/daara');
    } finally {
      setLoading(false);
    }
  };

  const fetchCommentaires = async () => {
    if (!id) return;
    try {
      const data = await daaraService.getCommentaires(id);
      setCommentaires(data);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleLike = async () => {
    if (!user || !id) {
      alert('Veuillez vous connecter pour aimer ce cours');
      return;
    }

    setLiking(true);
    try {
      await daaraService.like(id);
      fetchDaara();
    } catch (error: any) {
      console.error('Erreur:', error);
      if (error.response?.status === 401) {
        alert('Veuillez vous connecter pour aimer ce cours');
      }
    } finally {
      setLiking(false);
    }
  };

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !id) {
      alert('Veuillez vous connecter pour commenter');
      return;
    }

    if (!commentText.trim()) {
      alert('Veuillez entrer un commentaire');
      return;
    }

    setSubmittingComment(true);
    try {
      await daaraService.commenter(id, commentText.trim());
      setCommentText('');
      fetchCommentaires();
      fetchDaara();
    } catch (error: any) {
      console.error('Erreur:', error);
      if (error.response?.status === 401) {
        alert('Veuillez vous connecter pour commenter');
      } else {
        alert('Erreur lors de l\'envoi du commentaire');
      }
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDownloadPDF = () => {
    if (daara?.fichier_pdf) {
      window.open(daara.fichier_pdf, '_blank');
    }
  };

  const handleViewPDF = () => {
    if (daara?.fichier_pdf) {
      window.open(daara.fichier_pdf, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-20 w-20 border-4 border-green-200 border-t-green-700"></div>
      </div>
    );
  }

  if (!daara) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Cours non trouvé</h2>
          <button
            onClick={() => navigate('/daara')}
            className="px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors"
          >
            Retour aux cours
          </button>
        </div>
      </div>
    );
  }

  const jourLabel = JOURS.find(j => j.value === daara.jour)?.label || daara.jour;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-white via-green-50/30 to-green-50/50 py-12 sm:py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-green-200/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-green-300/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <button
            onClick={() => navigate('/daara')}
            className="mb-6 text-green-700 hover:text-green-800 font-semibold flex items-center space-x-2"
          >
            <span>←</span>
            <span>Retour aux cours</span>
          </button>

          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-green-100/50 overflow-hidden">
            {/* Photo principale */}
            <div className="relative h-64 md:h-96 bg-gradient-to-br from-green-600 via-green-700 to-green-800">
              {daara.photo_principale ? (
                <img
                  src={daara.photo_principale}
                  alt={daara.titre}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-8xl opacity-50 text-white">📚</span>
                </div>
              )}
              <div className="absolute top-4 left-4">
                <span className="px-4 py-2 bg-green-600/90 backdrop-blur-sm rounded-full text-sm font-bold text-white">
                  {jourLabel}
                </span>
              </div>
            </div>

            {/* Contenu */}
            <div className="p-6 md:p-8">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                {daara.titre}
              </h1>
              
              <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-sm text-gray-600 mb-6">
                <span className="flex items-center space-x-1 flex-shrink-0">
                  <span>👁️</span>
                  <span>{daara.nombre_vues} vues</span>
                </span>
                <span className="flex items-center space-x-1 flex-shrink-0">
                  <span>❤️</span>
                  <span>{daara.nombre_likes} likes</span>
                </span>
                <span className="flex items-center space-x-1 flex-shrink-0">
                  <span>💬</span>
                  <span>{daara.nombre_commentaires || 0} commentaires</span>
                </span>
                {daara.created_by && (
                  <span className="flex items-center space-x-1 flex-shrink-0 min-w-0">
                    <span>👤</span>
                    <span className="truncate max-w-[120px] sm:max-w-none">
                      {daara.created_by.first_name || daara.created_by.username}
                    </span>
                  </span>
                )}
              </div>

              <div className="prose max-w-none mb-8">
                <p className="text-gray-700 text-base sm:text-lg leading-relaxed break-words overflow-wrap-anywhere">
                  {daara.description}
                </p>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-6">
                <button
                  onClick={handleLike}
                  disabled={liking}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${
                    daara.is_liked
                      ? 'bg-red-500 hover:bg-red-600 text-white'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {liking ? '...' : daara.is_liked ? '❤️ Retirer le like' : '🤍 Aimer'}
                </button>
                
                {daara.fichier_pdf && (
                  <>
                    <button
                      onClick={handleViewPDF}
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-all duration-300 transform hover:scale-105"
                    >
                      📄 Voir le PDF
                    </button>
                    <button
                      onClick={handleDownloadPDF}
                      className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold transition-all duration-300 transform hover:scale-105"
                    >
                      ⬇️ Télécharger le PDF
                    </button>
                  </>
                )}
              </div>

              {/* Audio du cours */}
              {(daara.audio || daara.audio_cours) && (
                <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-6 border border-green-200 shadow-lg mb-8">
                  <div className="flex items-center space-x-4 mb-4">
                    <span className="text-3xl">🎧</span>
                    <h3 className="text-xl font-bold text-gray-900">Audio du cours</h3>
                  </div>
                  <audio
                    controls
                    className="w-full"
                    src={daara.audio_cours || daara.audio}
                  >
                    <source src={daara.audio_cours || daara.audio} type="audio/mpeg" />
                    <source src={daara.audio_cours || daara.audio} type="audio/wav" />
                    <source src={daara.audio_cours || daara.audio} type="audio/ogg" />
                    Votre navigateur ne supporte pas l'élément audio.
                  </audio>
                  <div className="mt-4">
                    <a
                      href={daara.audio_cours || daara.audio}
                      download
                      className="inline-flex items-center space-x-2 text-green-700 hover:text-green-800 font-semibold"
                    >
                      <span>⬇️</span>
                      <span>Télécharger l'audio</span>
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Commentaires */}
      <section className="relative bg-gradient-to-br from-green-50/30 via-white to-green-50/50 py-6 sm:py-8 md:py-10 overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 md:p-8 shadow-xl border border-green-100/50">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Commentaires ({daara.nombre_commentaires || 0})
            </h2>

            {/* Formulaire de commentaire */}
            {user ? (
              <form onSubmit={handleCommentSubmit} className="mb-8">
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Ajouter un commentaire..."
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white resize-none mb-4"
                />
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={submittingComment || !commentText.trim()}
                    className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submittingComment ? 'Envoi...' : 'Commenter'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-xl">
                <p className="text-gray-700">
                  <a href="/login" className="text-green-600 hover:text-green-700 font-semibold">
                    Connectez-vous
                  </a> pour commenter ce cours.
                </p>
              </div>
            )}

            {/* Liste des commentaires */}
            <div className="space-y-4">
              {commentaires.length > 0 ? (
                commentaires.map((commentaire) => (
                  <div
                    key={commentaire.id}
                    className="p-4 bg-gray-50 rounded-xl border border-gray-200"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-700 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                        {commentaire.user.first_name?.[0] || commentaire.user.username[0].toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="font-semibold text-gray-900">
                            {commentaire.user.first_name || commentaire.user.username}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(commentaire.created_at).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                        </div>
                        <p className="text-gray-700 break-words overflow-wrap-anywhere">
                          {commentaire.contenu}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>Aucun commentaire pour le moment. Soyez le premier à commenter !</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

